import { theme } from "@common/theme/theme"

/**
 * Attribution Ticket Constants
 * Centralized configuration for motifs, statuses, and urgency levels
 */

// Motif configurations for both student and tutor tickets
export const motifConfigs = {
    // Student motifs
    new_student: {
        label: "Nouvel élève",
        icon: "user-plus",
        color: theme.colors.success,
    },
    schedule_change: {
        label: "Demande de changement",
        icon: "calendar-alt",
        color: theme.colors.primary,
    },
    makeup_class: {
        label: "Rattrapage",
        icon: "redo-alt",
        color: theme.colors.accent,
    },
    subscription_resume: {
        label: "Reprise d'abonnement",
        icon: "sync",
        color: theme.colors.purple,
    },
    extra_hour: {
        label: "Heure supplémentaire",
        icon: "plus-circle",
        color: theme.colors.info,
    },
    new_need: {
        label: "Nouveau besoin",
        icon: "comment-dots",
        color: theme.colors.accent,
    },
    holiday_course: {
        label: "Stage",
        icon: "graduation-cap",
        color: theme.colors.primary,
    },
    potential_quality_issue: {
        label: "Problème de qualité potentiel",
        icon: "exclamation-triangle",
        color: theme.colors.warning,
    },
    unreported_absence: {
        label: "Absence non prévenue",
        icon: "user-times",
        color: theme.colors.error,
    },
    repeated_absences_first_session: {
        label: "Absences répétées en 1ère session",
        icon: "user-times",
        color: theme.colors.error,
    },
    parent_inactive_after_first_live: {
        label: "Parent inactif après premier live",
        icon: "user-clock",
        color: theme.colors.warning,
    },
    low_nps_score: {
        label: "Score NPS bas",
        icon: "poll",
        color: theme.colors.error,
    },
    quality_watch_onboarding: {
        label: "Qualité dégradée (1er mois)",
        icon: "search",
        color: theme.colors.warning,
    },
    payment_remove_lives: {
        label: "Retirer l'élève de ses Lives",
        icon: "user-slash",
        color: theme.colors.error,
    },
    payment_recovery: {
        label: "Recouvrement",
        icon: "gavel",
        color: theme.colors.error,
    },
    friendly_payment_overdue: {
        label: "Échéance amiable non respectée",
        icon: "handshake",
        color: theme.colors.error,
    },
    payment_pre_formal_notice: {
        label: "Appel avant mise en demeure",
        icon: "phone",
        color: theme.colors.error,
    },
    payment_termination: {
        label: "Résiliation + recouvrement",
        icon: "file-contract",
        color: theme.colors.error,
    },
    billing_adjustment: {
        label: "Ajuster la facture",
        icon: "file-invoice-dollar",
        color: theme.colors.info,
    },
    active_student_no_live: {
        label: "Élève actif sans live",
        icon: "user-clock",
        color: theme.colors.warning,
    },
    live_subscription_mismatch: {
        label: "Heures live ≠ abonnement",
        icon: "balance-scale",
        color: theme.colors.warning,
    },
    churn_request: {
        label: "Demande de résiliation",
        icon: "door-open",
        color: theme.colors.error,
    },
    // Tutor motifs
    punctual_absence: {
        label: "Absence ponctuelle",
        icon: "calendar-times",
        color: theme.colors.warning,
    },
    period_absence: {
        label: "Absence sur une période",
        icon: "calendar-minus",
        color: theme.colors.error,
    },
    permanent_absence: {
        label: "Absence définitive",
        icon: "calendar-day",
        color: theme.colors.error,
    },
    permanent_departure: {
        label: "Départ définitif",
        icon: "sign-out-alt",
        color: theme.colors.grey,
    },
    // Auto-détecté (cockpit Gestion Tuteurs, bo-web). Présent ici uniquement
    // pour que la table legacy affiche un libellé plutôt que la clé brute — il
    // n'entre pas dans `tutorMotifOptions`, qui liste ce qu'un admin ouvre.
    tutor_consecutive_no_shows: {
        label: "No-shows successifs",
        icon: "user-times",
        color: theme.colors.error,
    },
    blacklisting: {
        label: "Blacklistage",
        icon: "ban",
        color: theme.colors.error,
    },
    student_change_request: {
        label: "Demande de changement d'élève",
        icon: "exchange-alt",
        color: theme.colors.warning,
    },
    tutor_billing_adjustment: {
        label: "Ajuster la facture",
        icon: "file-invoice-dollar",
        color: theme.colors.info,
    },
}

// Status configurations
export const statusConfigs = {
    new: {
        label: "Nouveau",
        icon: "star",
        color: theme.colors.info,
        backgroundColor: theme.colors.infoLight,
    },
    todo: {
        label: "À traiter",
        icon: "flag",
        color: theme.colors.error,
        backgroundColor: theme.colors.errorLight,
    },
    in_progress: {
        label: "En cours",
        icon: "redo",
        color: theme.colors.accent,
        backgroundColor: theme.colors.accentLighter,
    },
    waiting_parents: {
        label: "En attente Parent",
        icon: "user",
        color: theme.colors.primary,
        backgroundColor: theme.colors.primaryLighter,
    },
    waiting_tutor: {
        label: "En attente Tuteur",
        icon: "user-graduate",
        color: theme.colors.purple,
        backgroundColor: theme.colors.purpleLight,
    },
    system_treated: {
        label: "Traité par le système",
        icon: "robot",
        color: theme.colors.success,
        backgroundColor: theme.colors.successLight,
    },
    surveillance: {
        label: "Surveillance",
        icon: "bolt",
        color: theme.colors.warning,
        backgroundColor: theme.colors.warningLight,
    },
    closed: {
        label: "Clôturé",
        icon: "check-circle",
        color: theme.colors.success,
        backgroundColor: theme.colors.successLight,
    },
}

// Urgency configurations
export const urgencyConfigs = {
    urgent: {
        label: "Urgent",
        icon: "exclamation-circle",
        color: theme.colors.white,
        backgroundColor: theme.colors.error,
        textColor: theme.colors.error,
        lightBackground: theme.colors.errorLight,
    },
    warning: {
        label: "Attention",
        icon: "exclamation-triangle",
        color: theme.colors.white,
        backgroundColor: theme.colors.warning,
        textColor: theme.colors.warning,
        lightBackground: theme.colors.warningLight,
    },
    normal: {
        label: "Normal",
        icon: "clock",
        color: theme.colors.grey,
        backgroundColor: theme.colors.lighter,
        textColor: theme.colors.grey,
        lightBackground: theme.colors.lighter,
    },
}

// Options arrays for dropdowns and filters
export const studentMotifOptions = Object.entries(motifConfigs)
    .filter(([key]) =>
        [
            "new_student",
            "schedule_change",
            "makeup_class",
            "subscription_resume",
            "extra_hour",
            "new_need",
            "holiday_course",
            "potential_quality_issue",
            "unreported_absence",
            "repeated_absences_first_session",
            "parent_inactive_after_first_live",
            "low_nps_score",
            "payment_remove_lives",
            "payment_recovery",
            "friendly_payment_overdue",
            "payment_pre_formal_notice",
            "payment_termination",
            "billing_adjustment",
            "active_student_no_live",
            "live_subscription_mismatch",
            "quality_watch_onboarding",
            "churn_request",
        ].includes(key)
    )
    .map(([value, config]) => ({
        value,
        label: config.label,
        iconName: config.icon,
        iconColor: config.color,
        iconBackgroundColor: config.color + "20",
    }))

export const tutorMotifOptions = Object.entries(motifConfigs)
    .filter(([key]) =>
        [
            "punctual_absence",
            "period_absence",
            "permanent_absence",
            "permanent_departure",
            "blacklisting",
            "student_change_request",
            "tutor_billing_adjustment",
        ].includes(key)
    )
    .map(([value, config]) => ({
        value,
        label: config.label,
        iconName: config.icon,
        iconColor: config.color,
        iconBackgroundColor: config.color + "20",
    }))

export const statusOptions = Object.entries(statusConfigs).map(
    ([value, config]) => ({
        value,
        label: config.label,
        icon: config.icon,
        color: config.color,
        backgroundColor: config.backgroundColor,
    })
)

export const urgencyOptions = Object.entries(urgencyConfigs).map(
    ([value, config]) => ({
        value,
        label: config.label,
        icon: config.icon,
        color: config.textColor,
        backgroundColor: config.lightBackground,
    })
)

// Status filter options for MenuFilter (open statuses only)
export const statusFilterOptions = [
    {
        title: "Statut",
        data: Object.entries(statusConfigs)
            .filter(([key]) => key !== "closed")
            .map(([value, config]) => ({
                value,
                label: config.label,
            })),
    },
]

// Helper functions
export const getMotifConfig = (motif) => {
    return (
        motifConfigs[motif] ?? {
            label: motif,
            icon: "question",
            color: theme.colors.grey,
        }
    )
}

export const getStatusConfig = (status) => {
    return (
        statusConfigs[status] ?? {
            label: status,
            icon: "question",
            color: theme.colors.grey,
            backgroundColor: theme.colors.lighter,
        }
    )
}

export const getUrgencyConfig = (urgency) => {
    return urgencyConfigs[urgency] ?? urgencyConfigs.normal
}

export const getDeadlineColor = (urgency) => {
    switch (urgency) {
        case "urgent":
            return theme.colors.error
        case "warning":
            return theme.colors.warning
        default:
            return theme.colors.grey
    }
}
